import { Box, Tabs } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export const WORKSPACE_CONTENT_PADDING = {
    inline: 2.5,
    inlineMd: 4,
} as const;

const hideScrollbar = {
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    '&::-webkit-scrollbar': {
        display: 'none',
        width: 0,
        height: 0,
    },
};

const Styled = {
    Workspace: styled(Box)(({ theme }) => ({
        width: '100%',
        minWidth: 0,
        maxWidth: 1180,
        marginInline: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        backgroundColor: 'transparent',
        border: 0,
        boxShadow: 'none',
        overflow: 'visible',
        ...hideScrollbar,

        [theme.breakpoints.up('md')]: {
            display: 'grid',
            gridTemplateColumns: '270px minmax(0, 1fr)',
            gridTemplateAreas: '"nav content"',
            alignItems: 'start',
            gap: theme.spacing(2.25),
        },

        '&.single-tab-workspace': {
            maxWidth: 920,
            display: 'block',
        },

        '&.full-width-workspace': {
            maxWidth: 'none',
        },
    })),

    Navigation: styled(Box)(({ theme }) => ({
        gridArea: 'nav',
        minWidth: 0,
        padding: 0,
        backgroundColor: 'transparent',
        border: 0,
        ...hideScrollbar,

        [theme.breakpoints.up('md')]: {
            position: 'sticky',
            top: theme.spacing(1),
        },
    })),

    NavigationTabs: styled(Tabs)(({ theme }) => ({
        minHeight: 0,
        maxWidth: '100%',
        padding: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        ...hideScrollbar,

        '.MuiTabs-scroller': {
            ...hideScrollbar,
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },

        '.MuiTabs-flexContainer': {
            gap: theme.spacing(1.25),
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },

        '.MuiTabs-flexContainerVertical': {
            gap: theme.spacing(1.25),
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },

        '.MuiTabs-indicator': {
            display: 'none',
        },

        '.MuiTabScrollButton-horizontal': {
            width: 28,
            display: 'flex',
            alignItems: 'center !important',
            justifyContent: 'center',
            padding: '0 !important',
            svg: {
                fontSize: 28,
            },
            '&.Mui-disabled': {
                display: 'none',
            },
        },

        '.MuiTab-root': {
            minHeight: 66,
            minWidth: 220,
            maxWidth: 'none',
            padding: theme.spacing(1.25, 1.5),
            color: theme.palette.text.primary,
            borderRadius: 12,
            border: '1px solid',
            borderColor:
                theme.palette.mode === 'dark'
                    ? '#ffffff12'
                    : theme.palette.border.light,
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : '#FFFFFF',
            boxShadow: 'none',
            transition: theme.transitions.create(
                ['background-color', 'border-color', 'box-shadow', 'transform'],
                { duration: 160 },
            ),
            flexDirection: 'row',
            justifyContent: 'flex-start',
            textAlign: 'right',

            '@media (hover: hover) and (pointer: fine)': {
                '&:not(.Mui-selected):hover': {
                    color: theme.palette.text.primary,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.025)
                            : '#FFFFFF',
                    // Keep hover visually quiet. The selected tab is the only
                    // item that should receive the primary-colored outline.
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? '#ffffff12'
                            : theme.palette.border.light,
                    boxShadow: 'none',
                    transform: 'none',
                },
            },

            '&.Mui-selected': {
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                borderColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.5 : 0.32,
                ),
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.17)
                        : '#FFFFFF',
                boxShadow: 'none',
            },

            '.MuiTab-iconWrapper': {
                width: 36,
                height: 36,
                minWidth: 36,
                display: 'grid',
                placeItems: 'center',
                margin: 0,
                marginInlineEnd: theme.spacing(1.25),
                borderRadius: 9,
                fontSize: 18,
                color: theme.palette.text.secondary,
                backgroundColor: 'transparent',
                border: 0,
            },

            '&.Mui-selected .MuiTab-iconWrapper': {
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                backgroundColor:
                    theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.16)
                        : alpha(theme.palette.primary.main, 0.055),
            },

            '.settings-tab-label': {
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(1),
            },

            '.settings-tab-label .MuiTypography-root': {
                minWidth: 0,
                fontSize: 14,
                fontWeight: 700,
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },

            '.settings-tab-arrow': {
                flexShrink: 0,
                fontSize: 20,
                color: theme.palette.text.light,
            },

            '&.Mui-selected .settings-tab-arrow': {
                color: theme.palette.primary.main,
            },
        },

        [theme.breakpoints.up('md')]: {
            '.MuiTab-root': {
                width: '100%',
                minWidth: 0,
            },
        },

        [theme.breakpoints.down('md')]: {
            '.MuiTab-root': {
                minWidth: 210,
            },
        },
    })),

    Content: styled(Box)(({ theme }) => ({
        gridArea: 'content',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        padding: theme.spacing(WORKSPACE_CONTENT_PADDING.inline),
        backgroundColor: theme.palette.background.paper,
        border: '1px solid',
        borderColor:
            theme.palette.mode === 'dark'
                ? '#ffffff12'
                : theme.palette.border.light,
        borderRadius: 18,
        boxShadow:
            theme.palette.mode === 'dark'
                ? `0 14px 36px ${alpha('#000000', 0.18)}`
                : `0 10px 32px ${alpha('#172033', 0.055)}`,
        ...hideScrollbar,

        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(3.5, WORKSPACE_CONTENT_PADDING.inlineMd),
        },
    })),
};

export { Styled };
